import { ErrorCode, type FileError } from 'react-dropzone';
import { isValidCNPJFormat } from 'src/utils/document';

export interface CSVRow {
  cnpj_fundo: string;
  subclasse?: string;
  serie?: string;
  quantidade?: string;
}

export interface CSVValidationResult {
  isValid: boolean;
  errors: string[];
  data?: CSVRow[];
}

const CSV_HEADER = ['cnpj_fundo', 'subclasse', 'serie', 'quantidade'];

export const CSV_CONSTRAINTS = {
  REQUIRED_COLUMNS: ['cnpj_fundo', 'subclasse', 'serie', 'quantidade'],
  REQUIRED_FIELD: 'cnpj_fundo',
  MAX_FILE_SIZE: 5000000,
} as const;

export enum ErrorType {
  FILE_TYPE = 'file-type',
  FILE_SIZE = 'file-size',
  FILE_COUNT = 'file-count',
  VALIDATION = 'validation',
  PROCESSING = 'processing',
}

export interface ValidationError {
  type: ErrorType;
  message: string;
  isSmallError: boolean;
}

export const validateCSV = async (file: File): Promise<CSVValidationResult> => {
  const errors: string[] = [];

  try {
    const text = await file.text();
    const lines = text.split('\n').filter((line) => line.trim() !== '');

    if (lines.length < 2) {
      return {
        isValid: false,
        errors: [
          'O arquivo deve conter pelo menos um cabeçalho e uma linha de dados',
        ],
      };
    }

    const CSV_SEPARATOR = lines[0].includes(';') ? ';' : ',';

    const headerValidation = validateCSVHeader(lines[0], CSV_SEPARATOR);
    if (!headerValidation.isValid) {
      return {
        isValid: false,
        errors: headerValidation.errors,
      };
    }

    const dataValidation = validateCSVData(lines.slice(1), CSV_SEPARATOR);
    if (!dataValidation.isValid) {
      errors.push(...dataValidation.errors);
    }

    return {
      isValid: errors.length === 0,
      errors,
      data: errors.length === 0 ? dataValidation.data : undefined,
    };
  } catch (error) {
    return {
      isValid: false,
      errors: ['Erro ao ler o arquivo CSV'],
    };
  }
};

const validateCSVHeader = (
  headerLine: string,
  separator = ',',
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  const header = headerLine
    .split(separator)
    .map((col) => col.trim().toLowerCase());

  if (!header.includes(CSV_CONSTRAINTS.REQUIRED_FIELD)) {
    errors.push(
      `Coluna obrigatória ausente: ${CSV_CONSTRAINTS.REQUIRED_FIELD}`,
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

const validateCSVData = (
  dataLines: string[],
  separator = ',',
): { isValid: boolean; errors: string[]; data?: CSVRow[] } => {
  const errors: string[] = [];
  const data: CSVRow[] = [];

  for (let i = 0; i < dataLines.length; i++) {
    const line = dataLines[i];
    const lineNumber = i + 2;
    const columns = line.split(separator).map((col) => col.trim());

    if (columns.length < 1) {
      errors.push(`Linha ${lineNumber}: Linha vazia ou sem colunas`);
      continue;
    }

    try {
      const row: CSVRow = {
        cnpj_fundo: columns[0],
        subclasse: columns[1] || undefined,
        serie: columns[2] || undefined,
        quantidade: columns[3] || undefined,
      };

      const rowValidation = validateCSVRow(row, lineNumber);
      if (rowValidation.errors.length > 0) {
        errors.push(...rowValidation.errors);
      } else {
        data.push(row);
      }
    } catch (error) {
      errors.push(`Linha ${lineNumber}: Erro ao processar dados`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    data: errors.length === 0 ? data : undefined,
  };
};

export const INVALID_CNPJ_FUNDO_MESSAGE = (lineNumber: number) =>
  `Linha ${lineNumber}: cnpj_fundo deve conter 14 caracteres (números, letras ou formatado)`;

export const validateCSVRow = (
  row: CSVRow,
  lineNumber: number,
): { errors: string[] } => {
  const errors: string[] = [];

  if (!row.cnpj_fundo) {
    errors.push(`Linha ${lineNumber}: cnpj_fundo é obrigatório`);
  } else if (!isValidCNPJFormat(row.cnpj_fundo.trim())) {
    errors.push(INVALID_CNPJ_FUNDO_MESSAGE(lineNumber));
  }

  if (!row.subclasse) {
    errors.push(`Linha ${lineNumber}: subclasse é obrigatório`);
  }

  if (!row.serie) {
    errors.push(`Linha ${lineNumber}: serie é obrigatório`);
  }

  if (!row.quantidade) {
    errors.push(`Linha ${lineNumber}: quantidade é obrigatório`);
  }

  return { errors };
};

export const processFileRejectionError = (
  error: FileError,
): ValidationError => {
  const errorCode = error.code as ErrorCode;

  switch (errorCode) {
    case ErrorCode.FileTooLarge:
      return {
        type: ErrorType.FILE_SIZE,
        message: `O documento excede o tamanho máximo. Max: ${CSV_CONSTRAINTS.MAX_FILE_SIZE / 1000000}MB`,
        isSmallError: true,
      };
    case ErrorCode.FileInvalidType:
      return {
        type: ErrorType.FILE_TYPE,
        message: 'Formato não suportado. Use apenas arquivos CSV',
        isSmallError: true,
      };
    case ErrorCode.TooManyFiles:
      return {
        type: ErrorType.FILE_COUNT,
        message: 'Selecione somente um documento',
        isSmallError: true,
      };
    default:
      return {
        type: ErrorType.PROCESSING,
        message: 'Erro ao processar arquivo',
        isSmallError: true,
      };
  }
};

export const isSmallError = (error: string): boolean => {
  const smallErrorPatterns = [
    'Formato não suportado',
    'excede o tamanho máximo',
    'Selecione somente um documento',
    'Erro ao processar arquivo',
    'Erro ao validar arquivo',
  ];

  return smallErrorPatterns.some((pattern) => error.includes(pattern));
};

export const shouldDisplayErrorInField = (
  validation: CSVValidationResult,
): boolean => {
  return validation.errors.length === 1 && isSmallError(validation.errors[0]);
};

export const shouldDisplayErrorInList = (
  validation: CSVValidationResult,
): boolean => {
  return validation.errors.length > 1;
};

export const getSuccessMessage = (data: CSVRow[]): string => {
  return `✓ ${data.length} ativo(s) encontrado(s)`;
};

export const canProcessFile = (file: File): boolean => {
  return file.type === 'text/csv' && file.size <= CSV_CONSTRAINTS.MAX_FILE_SIZE;
};
